/** Rich per-certificate content sourced from the Intygsläkaren reference implementation v79. */

export type LocalizedString = { sv: string; en: string };
export type LocalizedStringList = { sv: string[]; en: string[] };

export interface CertificatePriceItem {
  name: LocalizedString;
  price: LocalizedString;
  note: LocalizedString;
  /** Short unit line under the price, e.g. "per tillfälle" */
  unit?: LocalizedString;
  /** Checklist of what's included (typically on the registration / first visit card) */
  includes?: LocalizedStringList;
  /** Show a primary booking CTA on this price card */
  showBookingCta?: boolean;
}

export interface CertificatePricingSection {
  title: LocalizedString;
  subtitle?: LocalizedString;
  groups: Array<{
    title: LocalizedString;
    items: CertificatePriceItem[];
  }>;
  summary?: LocalizedString;
}

export interface CertificateProcessSection {
  title: LocalizedString;
  subtitle?: LocalizedString;
  items: Array<{
    title: LocalizedString;
    body: LocalizedString;
  }>;
}

export interface CertificateDetails {
  /** Extended lead paragraph (used for complex multi-step services like alkolås) */
  lead?: LocalizedString;
  /** What's included in the appointment / price */
  bullets?: LocalizedStringList;
  /** Preparation / what to bring */
  preparation?: LocalizedStringList;
  /** Process steps (replaces generic how-it-works if provided) */
  steps?: LocalizedStringList;
  /** Titled process steps with body copy (preferred over `steps` when present) */
  process?: CertificateProcessSection;
  /** Detailed price breakdown (e.g. alkolås) */
  prices?: Array<{
    name: LocalizedString;
    price: LocalizedString;
    note: LocalizedString;
  }>;
  /** Grouped pricing section with summary (e.g. droger & alkohol) */
  pricing?: CertificatePricingSection;
  /** FAQ accordion entries */
  faq?: Array<{ q: LocalizedString; a: LocalizedString }>;
}

export const certDetails: Record<string, CertificateDetails> = {
  /* ─── Körkort – högre behörighet ──────────────────────────── */
  "hogre-behorighet": {
    bullets: {
      sv: [
        "Fysiskt besök 1 100 kr inklusive syntest",
        "Videobesök 995 kr när ärendet kan bedömas digitalt",
        "Blodtryckskontroll och syntest ingår inte vid videobesök",
      ],
      en: [
        "In-person appointment 1,100 SEK including vision test",
        "Video appointment 995 SEK when suitable",
        "Blood pressure check and vision test not included in video appointments",
      ],
    },
    preparation: {
      sv: [
        "Giltig legitimation",
        "Glasögon eller kontaktlinser om du använder det",
        "Aktuell läkemedelslista om du tar läkemedel",
        "Brev eller blankett från Transportstyrelsen om du har fått ett",
      ],
      en: [
        "Valid ID",
        "Glasses or contact lenses if used",
        "Current medication list if applicable",
        "Any letter or form from the Swedish Transport Agency",
      ],
    },
    steps: {
      sv: [
        "Välj videobesök eller fysiskt besök och boka en tid online.",
        "Läkaren gör en medicinsk bedömning utifrån kraven för din behörighet.",
        "Intyget utfärdas och skickas till Transportstyrelsen.",
      ],
      en: [
        "Choose a video or in-person appointment and book online.",
        "The doctor carries out a medical assessment based on the licence requirements.",
        "The certificate is issued and sent to the Swedish Transport Agency.",
      ],
    },
  },

  /* ─── C och CE ─────────────────────────────────────────────── */
  "c-ce": {
    bullets: {
      sv: [
        "Fysiskt besök 1 100 kr inklusive syntest",
        "Videobesök 995 kr när ärendet kan bedömas digitalt",
        "Blodtryckskontroll och syntest ingår inte vid videobesök",
      ],
      en: [
        "In-person appointment 1,100 SEK including vision test",
        "Video appointment 995 SEK when suitable",
        "Blood pressure check and vision test not included in video appointments",
      ],
    },
    preparation: {
      sv: [
        "Giltig legitimation",
        "Glasögon eller kontaktlinser om du använder det",
        "Aktuell läkemedelslista om du tar läkemedel",
        "Brev eller blankett från Transportstyrelsen om du har fått ett",
      ],
      en: [
        "Valid ID",
        "Glasses or contact lenses if used",
        "Current medication list if applicable",
        "Any letter or form from the Swedish Transport Agency",
      ],
    },
    steps: {
      sv: [
        "Välj videobesök eller fysiskt besök och boka en tid online.",
        "Läkaren gör en medicinsk bedömning utifrån kraven för C- och CE-behörighet.",
        "Intyget utfärdas och skickas till Transportstyrelsen.",
      ],
      en: [
        "Choose a video or in-person appointment and book online.",
        "The doctor carries out a medical assessment based on C/CE requirements.",
        "The certificate is issued and sent to the Swedish Transport Agency.",
      ],
    },
  },

  /* ─── D och DE ─────────────────────────────────────────────── */
  "d-de": {
    bullets: {
      sv: [
        "Fysiskt besök 1 100 kr inklusive syntest",
        "Videobesök 995 kr med separata syn- och blodtrycksuppgifter",
        "Hörselprövning och övriga undersökningsmoment planeras enligt kraven",
      ],
      en: [
        "In-person appointment 1,100 SEK including vision test",
        "Video appointment 995 SEK with separate vision and blood pressure documentation",
        "Hearing assessment and other examinations planned per requirements",
      ],
    },
    preparation: {
      sv: [
        "Giltig legitimation",
        "Glasögon eller kontaktlinser om du använder det",
        "Aktuell läkemedelslista om du tar läkemedel",
        "Brev eller blankett från Transportstyrelsen om du har fått ett",
      ],
      en: [
        "Valid ID",
        "Glasses or contact lenses if used",
        "Current medication list if applicable",
        "Any letter or form from the Swedish Transport Agency",
      ],
    },
    steps: {
      sv: [
        "Välj videobesök eller fysiskt besök och boka en tid online.",
        "Läkaren gör en medicinsk bedömning utifrån kraven för buss. Hörseltest planeras separat.",
        "Intyget utfärdas och skickas till Transportstyrelsen.",
      ],
      en: [
        "Choose a video or in-person appointment and book online.",
        "The doctor assesses your fitness for the bus licence; the hearing test is arranged separately.",
        "The certificate is issued and sent to the Swedish Transport Agency.",
      ],
    },
  },

  /* ─── Buss och lastbil ─────────────────────────────────────── */
  "buss-lastbil": {
    bullets: {
      sv: [
        "För både C- och D-behörigheter",
        "Fysiskt besök 1 100 kr inklusive syntest",
        "Videobesök 995 kr – blodtryckskontroll och syntest ingår inte",
        "Hörselprövning planeras enligt kraven för buss",
      ],
      en: [
        "Covers both C and D licence categories",
        "In-person 1,100 SEK including vision test",
        "Video 995 SEK – blood pressure and vision checks not included",
        "Hearing assessment arranged per bus licence requirements",
      ],
    },
    preparation: {
      sv: [
        "Giltig legitimation",
        "Glasögon eller kontaktlinser om du använder det",
        "Aktuell läkemedelslista om du tar läkemedel",
        "Brev eller blankett från Transportstyrelsen om du har fått ett",
      ],
      en: [
        "Valid ID",
        "Glasses or contact lenses if used",
        "Current medication list if applicable",
        "Any letter or form from the Swedish Transport Agency",
      ],
    },
  },

  /* ─── Taxi ──────────────────────────────────────────────────── */
  taxi: {
    bullets: {
      sv: [
        "Fysiskt besök 1 100 kr inklusive syntest",
        "Videobesök 995 kr när ärendet kan bedömas digitalt",
        "Blodtryckskontroll och syntest ingår inte vid videobesök",
      ],
      en: [
        "In-person appointment 1,100 SEK including vision test",
        "Video appointment 995 SEK when suitable",
        "Blood pressure check and vision test not included in video appointments",
      ],
    },
    preparation: {
      sv: [
        "Giltig legitimation",
        "Glasögon eller kontaktlinser om du använder det",
        "Aktuell läkemedelslista om du tar läkemedel",
        "Brev eller blankett från Transportstyrelsen om du har fått ett",
      ],
      en: [
        "Valid ID",
        "Glasses or contact lenses if used",
        "Current medication list if applicable",
        "Any letter or form from the Swedish Transport Agency",
      ],
    },
  },

  /* ─── Alkolås ───────────────────────────────────────────────── */
  alkolas: {
    lead: {
      sv: "Vi hjälper dig med hela den medicinska delen: inskrivning, provtagningsplan, ordinarie prover och läkarintyget. Läkarkontakten kan ske digitalt och proverna lämnar du hos oss i Göteborg eller på en anvisad provtagningsplats nära dig.",
      en: "We handle the entire medical part: registration, a testing plan, regular samples and the certificate. The doctor consultation can be done digitally and samples are taken at our Gothenburg clinic or at an arranged facility near you.",
    },
    prices: [
      {
        name: { sv: "Alkolåsärende", en: "Alcohol interlock case" },
        price: { sv: "2 995 kr", en: "2,995 SEK" },
        note: {
          sv: "Inskrivning, ordinarie prover och läkarintyg ingår.",
          en: "Registration, ordinary samples and medical certificate included.",
        },
      },
      {
        name: { sv: "Ytterligare provtagning", en: "Additional sampling" },
        price: { sv: "1 500 kr", en: "1,500 SEK" },
        note: {
          sv: "Per tillfälle om fler prover behöver tas.",
          en: "Per occasion if additional samples are required.",
        },
      },
      {
        name: { sv: "Verifieringsprov", en: "Verification test" },
        price: { sv: "1 500 kr", en: "1,500 SEK" },
        note: {
          sv: "Tillkommer när ett provresultat behöver verifieras.",
          en: "Charged when a result needs to be verified.",
        },
      },
    ],
    steps: {
      sv: [
        "Starta ärendet och identifiera dig med BankID i journalsystemet.",
        "Ladda upp beslutet eller föreläggandet från Transportstyrelsen.",
        "Läkaren går igenom underlaget och planerar provtagningen.",
        "Du lämnar prover via Provio, Synlab eller Unilabs.",
        "När alla krav är uppfyllda färdigställer läkaren intyget.",
      ],
      en: [
        "Start your case and identify yourself with BankID in the patient system.",
        "Upload the decision or order from the Swedish Transport Agency.",
        "The doctor reviews the documents and plans the testing.",
        "You provide samples via Provio, Synlab or Unilabs.",
        "Once all requirements are met the doctor finalises the certificate.",
      ],
    },
    preparation: {
      sv: [
        "Transportstyrelsens beslut eller föreläggande",
        "Giltig legitimation",
        "Aktuell lista över läkemedel",
        "Eventuella relevanta journalhandlingar",
      ],
      en: [
        "The Swedish Transport Agency's decision or order",
        "Valid ID",
        "Current medication list",
        "Any relevant medical records",
      ],
    },
    faq: [
      {
        q: {
          sv: "Kan hela ärendet göras på distans?",
          en: "Can the entire case be handled remotely?",
        },
        a: {
          sv: "Läkarkontakt och uppföljning kan ske digitalt. Själva provtagningen sker alltid fysiskt på en anvisad provtagningsplats.",
          en: "Doctor consultations and follow-up can be done digitally. Sample collection is always done in person at an approved facility.",
        },
      },
      {
        q: {
          sv: "Ingår prover i priset?",
          en: "Are samples included in the price?",
        },
        a: {
          sv: "Ja. Priset 2 995 kr omfattar inskrivning, ordinarie prover och läkarintyg. Om fler provtagningstillfällen behövs kostar de 1 500 kr per tillfälle.",
          en: "Yes. The price of 2,995 SEK covers registration, ordinary samples and the certificate. Additional sampling occasions cost 1,500 SEK each.",
        },
      },
      {
        q: {
          sv: "Vad är ett verifieringsprov?",
          en: "What is a verification test?",
        },
        a: {
          sv: "Det är en kompletterande analys som kan behövas för att bekräfta ett provresultat. Kostnaden är 1 500 kr.",
          en: "It is a supplementary analysis that may be needed to confirm a result. The cost is 1,500 SEK.",
        },
      },
    ],
  },

  /* ─── Droger & alkohol ─────────────────────────────────────── */
  "droger-alkohol": {
    preparation: {
      sv: [
        "Transportstyrelsens beslut, föreläggande eller brev med samtliga sidor",
        "Giltig legitimation",
        "Aktuell läkemedelslista",
        "Eventuella relevanta journalhandlingar",
      ],
      en: [
        "The Swedish Transport Agency's decision, order or letter (all pages)",
        "Valid ID",
        "Current medication list",
        "Any relevant medical records",
      ],
    },
    pricing: {
      title: {
        sv: "Inskrivning, provtagning och avslutning",
        en: "Registration, testing and completion",
      },
      subtitle: {
        sv: "Det här kostar de olika delarna",
        en: "What the different parts cost",
      },
      groups: [
        {
          title: {
            sv: "Första läkarbesöket och provtagningen",
            en: "First doctor appointment and sampling",
          },
          items: [
            {
              name: { sv: "Inskrivning", en: "Registration" },
              price: { sv: "1 995 kr", en: "1,995 SEK" },
              note: {
                sv: "Inklusive första provtagningen.",
                en: "Including the first sampling occasion.",
              },
              includes: {
                sv: [
                  "Genomgång av ditt underlag och läkarbedömning",
                  "Första provtagningen",
                  "Personlig plan för fortsatt provtagning",
                ],
                en: [
                  "Review of your documentation and medical assessment",
                  "First sampling occasion",
                  "Personal plan for continued testing",
                ],
              },
              showBookingCta: true,
            },
          ],
        },
        {
          title: {
            sv: "Fortsatt provtagning och avslutning",
            en: "Continued testing and completion",
          },
          items: [
            {
              name: { sv: "Alkoholprov", en: "Alcohol test" },
              price: { sv: "1 250 kr", en: "1,250 SEK" },
              unit: { sv: "per tillfälle", en: "per occasion" },
              note: {
                sv: "Första provtagningen ingår i inskrivningen. Priset gäller efterföljande alkoholprov.",
                en: "The first sampling occasion is included in registration. This price applies to subsequent alcohol tests.",
              },
            },
            {
              name: { sv: "Drogprov – en drog", en: "Drug test – one drug" },
              price: { sv: "1 250 kr", en: "1,250 SEK" },
              unit: { sv: "per tillfälle", en: "per occasion" },
              note: {
                sv: "Analys av en drog vid provtagning efter det första tillfället.",
                en: "Analysis of one drug at sampling after the first occasion.",
              },
            },
            {
              name: { sv: "Ytterligare drog", en: "Additional drug" },
              price: { sv: "+ 250 kr", en: "+ 250 SEK" },
              unit: { sv: "per extra drog", en: "per extra drug" },
              note: {
                sv: "Tillägg vid fortsatt provtagning när fler droger ska analyseras vid samma tillfälle.",
                en: "Surcharge for continued testing when more drugs are analysed on the same occasion.",
              },
            },
            {
              name: { sv: "Kiral amfetaminanalys", en: "Chiral amphetamine analysis" },
              price: { sv: "+ 1 500 kr", en: "+ 1,500 SEK" },
              unit: { sv: "tillägg vid behov", en: "add-on when needed" },
              note: {
                sv: "Kompletterande analys vid behandling med amfetaminbaserade läkemedel, exempelvis Elvanse eller Attentin vid ADHD. Läkaren bedömer när analysen behövs.",
                en: "Supplementary analysis when you are treated with amphetamine-based medication, for example Elvanse or Attentin for ADHD. The doctor decides when the analysis is needed.",
              },
            },
            {
              name: { sv: "Avslutningsbesök och intyg", en: "Final appointment and certificate" },
              price: { sv: "1 995 kr", en: "1,995 SEK" },
              unit: { sv: "vid avslutning", en: "at completion" },
              note: {
                sv: "Avslutande läkarbedömning och signering av intyget när underlaget är komplett. Vi skickar pappersoriginalet till Transportstyrelsen samma dag och en elektronisk kopia till dig.",
                en: "Final medical assessment and signing of the certificate when the documentation is complete. We send the paper original to the Swedish Transport Agency the same day and an electronic copy to you.",
              },
            },
          ],
        },
      ],
      summary: {
        sv: "Totalkostnaden består av inskrivning på 1 995 kr, fortsatt provtagning enligt prislistan och avslutningsbesök med intyg på 1 995 kr. Första provtagningen ingår i inskrivningen. Kostnaden för fortsatta prover beror på antalet tillfällen och vilka analyser som behövs. Kiral amfetaminanalys är ett separat tillägg vid behov. Vi går igenom upplägg och kostnader vid inskrivningen.",
        en: "The total cost consists of registration at 1,995 SEK, continued testing according to the price list, and a final appointment with certificate at 1,995 SEK. The first sampling occasion is included in registration. The cost of further tests depends on the number of occasions and which analyses are needed. Chiral amphetamine analysis is a separate add-on when needed. We go through the plan and costs at registration.",
      },
    },
    process: {
      title: { sv: "Så går det till", en: "How it works" },
      subtitle: {
        sv: "Från inskrivning till intyg",
        en: "From registration to certificate",
      },
      items: [
        {
          title: { sv: "Boka inskrivning", en: "Book registration" },
          body: {
            sv: "Boka ett läkarbesök för alkohol- eller drogintyg. Ha alla sidor av Transportstyrelsens beslut och sista datum för intyget tillgängliga.",
            en: "Book a doctor appointment for an alcohol or drug certificate. Have all pages of the Swedish Transport Agency decision and the certificate deadline available.",
          },
        },
        {
          title: { sv: "Vi går igenom ditt beslut", en: "We review your decision" },
          body: {
            sv: "Läkaren granskar underlaget och din medicinska situation. Beslut, läkemedelslista och andra hälsouppgifter lämnar du via journalsystemet enligt instruktionerna inför besöket.",
            en: "The doctor reviews the documentation and your medical situation. You submit the decision, medication list and other health information via the patient system according to the instructions before your visit.",
          },
        },
        {
          title: { sv: "Du får en personlig provtagningsplan", en: "You receive a personal testing plan" },
          body: {
            sv: "Första provtagningen ingår i inskrivningen. Vi går igenom vilka analyser som behövs, hur fortsatt provtagning ska planeras och vad den kostar. Du får remiss och instruktioner om var och hur du lämnar prover.",
            en: "The first sampling occasion is included in registration. We go through which analyses are needed, how continued testing should be planned and what it costs. You receive a referral and instructions on where and how to provide samples.",
          },
        },
        {
          title: { sv: "Lämna prover under perioden", en: "Provide samples during the period" },
          body: {
            sv: "Proverna tas fysiskt hos oss i Göteborg eller på en anvisad provtagningsplats. Följ instruktionerna för ditt ärende. Läkaren följer provsvaren och bedömer om underlaget behöver kompletteras.",
            en: "Samples are taken in person at our Gothenburg clinic or at an arranged sampling facility. Follow the instructions for your case. The doctor monitors the results and assesses whether further documentation is needed.",
          },
        },
        {
          title: { sv: "Avslutningsbesök och intyg", en: "Final appointment and certificate" },
          body: {
            sv: "När provtagningsperioden är genomförd och underlaget är komplett gör läkaren sin avslutande bedömning. Avslutningsbesöket kostar 1 995 kr. Intyget signeras vid besöket och vi skickar det signerade originalet i pappersform till Transportstyrelsen samma dag. Du får även en elektronisk kopia.",
            en: "When the testing period is complete and the documentation is ready, the doctor makes the final assessment. The final appointment costs 1,995 SEK. The certificate is signed at the visit and we send the signed paper original to the Swedish Transport Agency the same day. You also receive an electronic copy.",
          },
        },
      ],
    },
  },

  /* ─── Salivtest ─────────────────────────────────────────────── */
  salivtest: {
    bullets: {
      sv: [
        "Salivtest med sex analyser: amfetamin, bensodiazepiner, THC, kokain, ecstasy och opiater",
        "Laboratorieanalys i samarbete med Unilabs",
        "Privata provpaket bokas direkt",
      ],
      en: [
        "Saliva test with six analyses: amphetamine, benzodiazepines, THC, cocaine, ecstasy and opiates",
        "Laboratory analysis in collaboration with Unilabs",
        "Private test packages booked directly",
      ],
    },
    preparation: {
      sv: [
        "Giltig legitimation",
        "Undvik att äta, dricka eller borsta tänderna 10 minuter före provtagning",
      ],
      en: [
        "Valid ID",
        "Avoid eating, drinking or brushing teeth 10 minutes before sampling",
      ],
    },
  },

  /* ─── Urinprov ──────────────────────────────────────────────── */
  urinprov: {
    bullets: {
      sv: [
        "Urinprov med fyra analyser: opiater, kokain, amfetamin och THC",
        "Laboratorieanalys i samarbete med Unilabs",
        "Privata provpaket bokas direkt",
      ],
      en: [
        "Urine test with four analyses: opiates, cocaine, amphetamine and THC",
        "Laboratory analysis in collaboration with Unilabs",
        "Private test packages booked directly",
      ],
    },
    preparation: {
      sv: ["Giltig legitimation"],
      en: ["Valid ID"],
    },
  },

  /* ─── Körkort diabetes ─────────────────────────────────────── */
  "korkort-diabetes": {
    lead: {
      sv: "Vid diabetes beror bedömningen bland annat på behandlingen, risken för lågt blodsocker och vilken körkortsbehörighet ärendet gäller. Vid besöket går läkaren igenom din diabetes och uppgifterna som behövs för intyget.",
      en: "For diabetes the assessment depends on your treatment, risk of low blood sugar and the type of licence. The doctor reviews your diabetes and the information required for the certificate.",
    },
    preparation: {
      sv: [
        "Brevet från Transportstyrelsen med samtliga sidor och eventuell sista svarsdag",
        "Befintligt intyg eller den blankett du har fått",
        "Aktuell läkemedelslista och uppgifter från din diabetesuppföljning",
      ],
      en: [
        "The letter from the Swedish Transport Agency (all pages), including any deadline",
        "Existing certificate or the form you have received",
        "Current medication list and details from your diabetes follow-up",
      ],
    },
    steps: {
      sv: [
        "Boka din tid och välj videobesök eller besök på mottagningen i Göteborg.",
        "Läkaren går igenom din diabetes och gör den medicinska bedömningen.",
        "Intyget utfärdas och skickas till Transportstyrelsen.",
      ],
      en: [
        "Book a video or in-person appointment.",
        "The doctor reviews your diabetes and makes the medical assessment.",
        "The certificate is issued and sent to the Swedish Transport Agency.",
      ],
    },
  },

  /* ─── Körkort hjärta ───────────────────────────────────────── */
  "korkort-hjarta": {
    lead: {
      sv: "Hjärtsjukdom, symtom och hur behandlingen fungerar kan påverka körkortsbedömningen. Kraven varierar mellan olika tillstånd och behörigheter. Vid besöket går läkaren igenom frågeställningen och dina medicinska underlag.",
      en: "Heart disease, symptoms and how treatment is working can all affect the driving licence assessment. Requirements vary between conditions and licence types. The doctor will review your situation and medical records.",
    },
    preparation: {
      sv: [
        "Transportstyrelsens brev eller föreläggande med hela frågeställningen",
        "Tidigare intyg om du har ett",
        "Läkemedelslista och relevanta utlåtanden från din hjärtmottagning",
      ],
      en: [
        "The Transport Agency's letter or order with the full question",
        "Existing certificate if you have one",
        "Medication list and relevant statements from your cardiology clinic",
      ],
    },
    steps: {
      sv: [
        "Boka din tid och välj videobesök eller besök på mottagningen i Göteborg.",
        "Läkaren går igenom din hjärtsjukdom och gör den medicinska bedömningen.",
        "Intyget utfärdas och skickas till Transportstyrelsen.",
      ],
      en: [
        "Book a video or in-person appointment.",
        "The doctor reviews your heart condition and makes the medical assessment.",
        "The certificate is issued and sent to the Swedish Transport Agency.",
      ],
    },
  },

  /* ─── Körkort adhd/övriga ──────────────────────────────────── */
  "korkort-adhd": {
    preparation: {
      sv: [
        "Blanketten, intyget eller brevet som visar vad som efterfrågas",
        "Alla sidor med frågor och eventuella krav på läkarens specialistkompetens",
        "Sista svarsdag om den finns angiven",
      ],
      en: [
        "The form, certificate or letter showing what is requested",
        "All pages with questions and any requirements for the doctor's specialist competence",
        "Deadline if one is stated",
      ],
    },
  },

  /* ─── Sjöfolksintyg ─────────────────────────────────────────── */
  sjofolksintyg: {
    bullets: {
      sv: [
        "Läkarundersökning och sjöfolksintyg enligt Manila/STCW",
        "Anslutna till Sjöläkarwebben – intyget registreras i sjömansregistret",
        "Hörseltest vid behov + 395 kr – totalt 1 495 kr",
        "Intyg efter besöket om inga extra åtgärder krävs",
      ],
      en: [
        "Medical examination and seafarer certificate under Manila/STCW",
        "Connected to Sjöläkarwebben – certificate registered in the seamens register",
        "Hearing test if needed + 395 SEK – total 1,495 SEK",
        "Certificate issued after the appointment if no further action is needed",
      ],
    },
    preparation: {
      sv: [
        "Giltig legitimation",
        "Glasögon eller kontaktlinser om du använder det",
        "Tidigare sjöfolksintyg om du har ett",
        "Lista på aktuella läkemedel",
      ],
      en: [
        "Valid ID",
        "Glasses or contact lenses if used",
        "Previous seafarer certificate if you have one",
        "Current medication list",
      ],
    },
    steps: {
      sv: [
        "Boka ett fysiskt besök på vår mottagning i Göteborg.",
        "Läkaren genomför undersökningen och bedömer din lämplighet.",
        "Intyget registreras i Sjöläkarwebben och du får det direkt.",
      ],
      en: [
        "Book an in-person appointment at our Gothenburg clinic.",
        "The doctor conducts the examination and assesses your fitness.",
        "The certificate is registered in Sjöläkarwebben and issued to you directly.",
      ],
    },
  },

  /* ─── Offshore Norge ─────────────────────────────────────────── */
  offshore: {
    bullets: {
      sv: ["Vikt", "Längd", "Midjemått", "BMI", "Blodtryck", "Urinsticka", "Läkarbesök"],
      en: ["Weight", "Height", "Waist circumference", "BMI", "Blood pressure", "Urine dipstick", "Doctor consultation"],
    },
    preparation: {
      sv: [
        "Giltig legitimation",
        "Tidigare offshoreintyg om du har ett",
        "Aktuell läkemedelslista",
        "Eventuella relevanta journalhandlingar",
      ],
      en: [
        "Valid ID",
        "Previous offshore certificate if you have one",
        "Current medication list",
        "Any relevant medical records",
      ],
    },
    steps: {
      sv: [
        "Skicka en förfrågan så planerar vi besöket.",
        "Fysisk undersökning på vår mottagning i Göteborg.",
        "Godkänt intyg utfärdas av en petroleumslege godkänd av Statsforvaltaren i Rogaland.",
      ],
      en: [
        "Send an enquiry and we will plan the appointment.",
        "In-person examination at our Gothenburg clinic.",
        "Certificate issued by a physician approved by Statsforvaltaren i Rogaland.",
      ],
    },
  },

  /* ─── OEUK offshore ──────────────────────────────────────────── */
  oguk: {
    bullets: {
      sv: ["Vikt", "Längd", "Midjemått", "BMI", "Blodtryck", "Urinsticka", "Läkarbesök"],
      en: ["Weight", "Height", "Waist circumference", "BMI", "Blood pressure", "Urine dipstick", "Doctor consultation"],
    },
    preparation: {
      sv: [
        "Giltig legitimation",
        "Tidigare OEUK-intyg om du har ett",
        "Aktuell läkemedelslista",
        "Eventuella relevanta journalhandlingar",
      ],
      en: [
        "Valid ID",
        "Previous OEUK certificate if you have one",
        "Current medication list",
        "Any relevant medical records",
      ],
    },
    steps: {
      sv: [
        "Skicka en kontaktförfrågan så planerar vi besöket.",
        "Fysisk undersökning på vår mottagning i Göteborg.",
        "OEUK-intyget utfärdas efter medicinsk bedömning.",
      ],
      en: [
        "Send an enquiry and we will plan the appointment.",
        "In-person examination at our Gothenburg clinic.",
        "OEUK certificate issued after medical assessment.",
      ],
    },
  },

  /* ─── Au pair ────────────────────────────────────────────────── */
  "au-pair": {
    bullets: {
      sv: [
        "Läkarbesök online eller på mottagningen",
        "Genomgång av organisationens blankett",
        "Medicinsk bedömning och hälsointyg när underlaget är komplett",
      ],
      en: [
        "Doctor consultation online or in person",
        "Review of the organisation's form",
        "Medical assessment and health certificate when documentation is complete",
      ],
    },
    preparation: {
      sv: [
        "Giltig legitimation",
        "Au pair-organisationens blankett och samtliga instruktioner",
        "Uppgifter om destination, avresedatum och sista dag för intyget",
        "Läkemedelslista samt vaccinationsuppgifter och tidigare provsvar om de efterfrågas",
      ],
      en: [
        "Valid ID",
        "The au pair organisation's form and all instructions",
        "Destination, travel date and certificate deadline",
        "Medication list and vaccination records / previous test results if requested",
      ],
    },
    steps: {
      sv: [
        "Boka läkarbesöket online eller på mottagningen i Göteborg.",
        "Ha organisationens blankett och instruktioner redo till besöket.",
        "Läkaren fyller i blanketten och utfärdar intyget.",
      ],
      en: [
        "Book the appointment online or at our Gothenburg clinic.",
        "Have the organisation's form and instructions ready for the visit.",
        "The doctor completes the form and issues the certificate.",
      ],
    },
  },

  /* ─── Förstadagsintyg ─────────────────────────────────────────── */
  forstadagsintyg: {
    bullets: {
      sv: [
        "Läkarbesök online eller på mottagningen",
        "Bedömning av symtom och arbetsförmåga",
        "Läkarintyg när det finns medicinskt underlag",
      ],
      en: [
        "Doctor consultation online or in person",
        "Assessment of symptoms and work capacity",
        "Medical certificate when there is sufficient medical basis",
      ],
    },
    preparation: {
      sv: [
        "Giltig legitimation",
        "Arbetsgivarens begäran om förstadagsintyg och eventuell blankett",
        "Uppgifter om dina arbetsuppgifter, symtom och första sjukdag",
        "Aktuell läkemedelslista och relevanta medicinska uppgifter",
      ],
      en: [
        "Valid ID",
        "Your employer's request for a first-day certificate and any form",
        "Details of your job, symptoms and first sick day",
        "Current medication list and relevant medical details",
      ],
    },
    steps: {
      sv: [
        "Boka en läkarbedömning online eller på mottagningen – boka gärna den dag du blir sjuk.",
        "Läkaren går igenom dina besvär och bedömer din arbetsförmåga.",
        "Intyget utfärdas och kan skickas till din arbetsgivare.",
      ],
      en: [
        "Book a medical assessment online or in person – ideally on the day you fall ill.",
        "The doctor reviews your symptoms and assesses your work capacity.",
        "The certificate is issued and can be sent to your employer.",
      ],
    },
  },

  /* ─── Utlandsstudier/arbete ─────────────────────────────────── */
  utlandsstudier: {
    bullets: {
      sv: [
        "Läkarbesök online eller på mottagningen",
        "Genomgång av blankett och intygskrav",
        "Medicinsk bedömning och intyg när underlaget är komplett",
      ],
      en: [
        "Doctor consultation online or in person",
        "Review of form and certificate requirements",
        "Medical assessment and certificate when documentation is complete",
      ],
    },
    preparation: {
      sv: [
        "Giltig legitimation och den aktuella intygsblanketten",
        "Instruktioner om undersökningar, språk och sista inlämningsdag",
        "Aktuell läkemedelslista och relevanta medicinska underlag",
        "Tidigare provsvar och vaccinationsuppgifter om mottagaren efterfrågar dem",
      ],
      en: [
        "Valid ID and the current certificate form",
        "Instructions about examinations, language requirements and deadline",
        "Current medication list and relevant medical documentation",
        "Previous test results and vaccination records if requested",
      ],
    },
    steps: {
      sv: [
        "Boka läkarbesöket online eller på mottagningen i Göteborg.",
        "Ta med blanketten och instruktionerna från skolan eller arbetsgivaren.",
        "Läkaren gör bedömningen och utfärdar intyget.",
      ],
      en: [
        "Book the consultation online or at our Gothenburg clinic.",
        "Bring the form and instructions from the school or employer.",
        "The doctor makes the assessment and issues the certificate.",
      ],
    },
  },

  /* ─── Nattarbete ─────────────────────────────────────────────── */
  nattarbete: {
    bullets: {
      sv: [
        "Fysiskt läkarbesök i Göteborg",
        "Genomgång av hälsa, sömn och arbetstider",
        "Blodtryckskontroll",
        "Provtagning enligt kontrollens upplägg",
        "Medicinsk bedömning och återkoppling",
      ],
      en: [
        "In-person appointment in Gothenburg",
        "Review of health, sleep and working hours",
        "Blood pressure check",
        "Sampling per the examination protocol",
        "Medical assessment and feedback",
      ],
    },
    preparation: {
      sv: [
        "Giltig legitimation",
        "Arbetsgivarens beställning och uppgifter om arbetet",
        "Schema eller beskrivning av dina nattpass och din återhämtning",
        "Aktuell läkemedelslista och relevanta tidigare undersökningsresultat",
      ],
      en: [
        "Valid ID",
        "Employer's order and details about your job",
        "Schedule or description of your night shifts and recovery",
        "Current medication list and relevant previous examination results",
      ],
    },
    steps: {
      sv: [
        "Boka ett fysiskt besök på vår mottagning i Göteborg.",
        "Läkaren går igenom din hälsa, arbetstider och sömn.",
        "Du får en medicinsk bedömning och återkoppling.",
      ],
      en: [
        "Book an in-person appointment at our Gothenburg clinic.",
        "The doctor reviews your health, working hours and sleep.",
        "You receive a medical assessment and feedback.",
      ],
    },
  },

  /* ─── Fit-to-fly ─────────────────────────────────────────────── */
  "fit-for-fly": {
    bullets: {
      sv: [
        "Säker dokumenthantering i journalen",
        "Individuell medicinsk bedömning",
        "Tydlig information om eventuella kompletteringar",
      ],
      en: [
        "Secure handling of documentation in the patient record",
        "Individual medical assessment",
        "Clear information about any supplementary steps",
      ],
    },
    preparation: {
      sv: [
        "Giltig legitimation",
        "Flygbolagets blankett eller krav (om du har en)",
        "Aktuell läkemedelslista",
        "Relevanta medicinska underlag",
      ],
      en: [
        "Valid ID",
        "Airline form or requirements if you have one",
        "Current medication list",
        "Relevant medical documentation",
      ],
    },
  },

  /* ─── Inställd resa ──────────────────────────────────────────── */
  "installd-resa": {
    bullets: {
      sv: [
        "Säker dokumenthantering i journalen",
        "Individuell medicinsk bedömning",
        "Tydlig information om eventuella kompletteringar",
      ],
      en: [
        "Secure handling of documentation in the patient record",
        "Individual medical assessment",
        "Clear information about any supplementary steps",
      ],
    },
    preparation: {
      sv: [
        "Giltig legitimation",
        "Försäkringsbolagets blankett eller krav (om du har en)",
        "Uppgifter om resa, bokningsdatum och sjukdomsdebut",
        "Aktuell läkemedelslista och relevanta medicinska underlag",
      ],
      en: [
        "Valid ID",
        "Insurer's form or requirements if you have one",
        "Travel details, booking date and date of illness",
        "Current medication list and relevant medical documentation",
      ],
    },
  },

  /* ─── Reseintyg gravid ─────────────────────────────────────── */
  "reseintyg-gravid": {
    bullets: {
      sv: [
        "Säker dokumenthantering i journalen",
        "Individuell medicinsk bedömning",
        "Tydlig information om eventuella kompletteringar",
      ],
      en: [
        "Secure handling of documentation in the patient record",
        "Individual medical assessment",
        "Clear information about any supplementary steps",
      ],
    },
    preparation: {
      sv: [
        "Giltig legitimation",
        "Flygbolagets blankett eller krav",
        "Uppgifter om graviditetsvecka och planerad resa",
        "Aktuell läkemedelslista och relevanta medicinska underlag",
      ],
      en: [
        "Valid ID",
        "Airline form or requirements",
        "Gestational week and planned travel details",
        "Current medication list and relevant medical documentation",
      ],
    },
  },

  /* ─── Försäkringsintyg ─────────────────────────────────────── */
  "intyg-forsakring": {
    bullets: {
      sv: [
        "Fysiskt läkarbesök på mottagningen i Göteborg",
        "Genomgång av försäkringsbolagets blankett för tjänstepension",
        "Medicinsk bedömning och läkarintyg när underlaget är komplett",
      ],
      en: [
        "In-person appointment at our Gothenburg clinic",
        "Review of the insurance company's occupational pension form",
        "Medical assessment and certificate when documentation is complete",
      ],
    },
    preparation: {
      sv: [
        "Giltig legitimation",
        "Försäkringsbolagets blankett för tjänstepension",
        "Aktuell läkemedelslista",
        "Relevanta medicinska underlag och journalhandlingar",
      ],
      en: [
        "Valid ID",
        "Insurance company's occupational pension form",
        "Current medication list",
        "Relevant medical records and documentation",
      ],
    },
  },

  /* ─── Invaliditetsintyg ─────────────────────────────────────── */
  invaliditetsintyg: {
    bullets: {
      sv: [
        "Fysiskt läkarbesök på mottagningen i Göteborg",
        "Undersökning och bedömning av funktionsnedsättning",
        "Genomgång av relevanta journalunderlag och försäkringsbolagets frågor",
      ],
      en: [
        "In-person appointment at our Gothenburg clinic",
        "Examination and assessment of functional impairment",
        "Review of relevant medical records and insurer's questions",
      ],
    },
    preparation: {
      sv: [
        "Giltig legitimation",
        "Försäkringsbolagets blankett och samtliga instruktioner",
        "Relevanta journalunderlag och utlåtanden från specialister",
        "Aktuell läkemedelslista",
      ],
      en: [
        "Valid ID",
        "Insurer's form and all instructions",
        "Relevant medical records and specialist statements",
        "Current medication list",
      ],
    },
  },

  /* ─── Fallskärm ──────────────────────────────────────────────── */
  fallskarmsintyg: {
    bullets: {
      sv: [
        "Fysiskt besök och undersökning hos legitimerad läkare i Göteborg",
        "Genomgång av hälsodeklaration och intygsblankett",
        "Urinsticka",
        "Blodvärde (Hb)",
        "Medicinsk bedömning och intyg när underlaget är komplett",
      ],
      en: [
        "In-person examination by a licensed doctor in Gothenburg",
        "Review of health declaration and certificate form",
        "Urine dipstick",
        "Haemoglobin (Hb) test",
        "Medical assessment and certificate when documentation is complete",
      ],
    },
    preparation: {
      sv: [
        "Den aktuella intygsblanketten från fallskärmsklubben eller förbundet",
        "Ifylld hälsodeklaration (fylls i före besöket)",
        "Giltig legitimation",
        "Klubbens eller förbundets instruktioner",
        "Aktuell läkemedelslista och relevanta journalunderlag",
        "Datum för kursstart eller när intyget senast behövs",
      ],
      en: [
        "The current certificate form from your skydiving club or federation",
        "Completed health declaration (fill in before the appointment)",
        "Valid ID",
        "Club or federation instructions",
        "Current medication list and relevant medical records",
        "Course start date or certificate deadline",
      ],
    },
    faq: [
      {
        q: {
          sv: "Vad ska jag ta med till besöket?",
          en: "What should I bring to the appointment?",
        },
        a: {
          sv: "Ta med den aktuella intygsblanketten och din ifyllda hälsodeklaration. Hälsodeklarationen ska vara ifylld före besöket. Ta även med giltig legitimation och klubbens instruktioner.",
          en: "Bring the current certificate form and your completed health declaration. The health declaration should be filled in before the appointment. Also bring valid ID and your club's instructions.",
        },
      },
      {
        q: {
          sv: "Behöver alla som ska hoppa fallskärm ett läkarintyg?",
          en: "Do all skydivers need a medical certificate?",
        },
        a: {
          sv: "Kontrollera med din klubb vad som gäller för just din hoppning. En fallskärmsutbildning, ett uppdrag som instruktör och ett tandemhopp kan ha olika krav.",
          en: "Check with your club what applies to your specific jump. A skydiving course, instructor role and tandem jump may have different requirements.",
        },
      },
      {
        q: {
          sv: "Hur långt före kursstart bör jag boka?",
          en: "How far in advance should I book before the course starts?",
        },
        a: {
          sv: "För nya elever rekommenderar Svenska Fallskärmsförbundet läkarundersökning minst fyra veckor före kursstart, så att eventuella kompletteringar hinner bli klara.",
          en: "For new students Svenska Fallskärmsförbundet recommends a medical examination at least four weeks before the course start, to allow time for any follow-up.",
        },
      },
    ],
  },

  /* ─── Tävlingsintyg ──────────────────────────────────────────── */
  tavlingsintyg: {
    bullets: {
      sv: [
        "Läkarbesök och individuell medicinsk bedömning",
        "Genomgång av arrangörens intygskrav",
        "Intyg när det finns tillräckligt medicinskt underlag",
      ],
      en: [
        "Doctor consultation and individual medical assessment",
        "Review of organiser's certificate requirements",
        "Certificate when there is sufficient medical basis",
      ],
    },
    preparation: {
      sv: [
        "Giltig legitimation",
        "Arrangörens eller förbundets aktuella intygsblankett",
        "Uppgifter om idrott, tävling, datum och sista inlämningsdag",
        "Instruktioner om språk, giltighet och eventuella undersökningar",
        "Läkemedelslista och relevanta medicinska underlag",
      ],
      en: [
        "Valid ID",
        "Current certificate form from the organiser or federation",
        "Sport, competition, date and submission deadline",
        "Instructions about language requirements, validity and any examinations",
        "Medication list and relevant medical documentation",
      ],
    },
    faq: [
      {
        q: {
          sv: "När passar ett videobesök?",
          en: "When is a video appointment suitable?",
        },
        a: {
          sv: "Video kan passa när läkaren kan bedöma ärendet digitalt och mottagaren godtar upplägget. Om intyget kräver EKG, spirometri eller annan fysisk undersökning behöver dessa moment göras på mottagning.",
          en: "A video appointment can work when the doctor can assess the case digitally and the recipient accepts this. If the certificate requires ECG, spirometry or other physical examination, these must be done in person.",
        },
      },
      {
        q: {
          sv: "Kan det tillkomma kostnader för undersökningar och prover?",
          en: "Can there be additional costs for examinations or tests?",
        },
        a: {
          sv: "Ja. Om röntgen, EKG eller blodprover behövs kan kostnader tillkomma utöver priset från 1 100 kr. Du får information om kostnaden innan undersökningarna planeras.",
          en: "Yes. If X-ray, ECG or blood tests are needed, additional costs may apply beyond the base price from 1,100 SEK. You will be informed of the cost before any examinations are planned.",
        },
      },
    ],
  },

  /* ─── Adoptionsintyg ─────────────────────────────────────────── */
  adoptionsintyg: {
    bullets: {
      sv: [
        "Fysiskt besök och undersökning hos legitimerad läkare i Göteborg",
        "Genomgång av hälsodeklaration och intygsblankett",
        "Provtagning för adoptionsintyget",
        "Medicinsk bedömning och intyg när nödvändiga provsvar finns",
      ],
      en: [
        "In-person examination by a licensed doctor in Gothenburg",
        "Review of health declaration and certificate form",
        "Sampling for the adoption certificate",
        "Medical assessment and certificate when all results are available",
      ],
    },
    preparation: {
      sv: [
        "Giltig legitimation",
        "Ifylld hälsodeklaration och aktuell läkarintygsblankett",
        "Instruktioner från familjerätten, adoptionsorganisationen eller mottagarlandet",
        "Aktuell läkemedelslista och relevanta journalhandlingar",
        "Uppgifter om språk, sista inlämningsdag och eventuella särskilda provkrav",
      ],
      en: [
        "Valid ID",
        "Completed health declaration and current medical certificate form",
        "Instructions from the family court, adoption organisation or receiving country",
        "Current medication list and relevant medical records",
        "Language requirements, submission deadline and any special sampling requirements",
      ],
    },
    faq: [
      {
        q: {
          sv: "Ingår provtagningen i priset?",
          en: "Is sampling included in the price?",
        },
        a: {
          sv: "Ja. Provtagningen för adoptionsintyget ingår i priset 2 600 kr, tillsammans med läkarundersökning och intyg.",
          en: "Yes. Sampling for the adoption certificate is included in the price of 2,600 SEK, together with the medical examination and certificate.",
        },
      },
      {
        q: {
          sv: "Vilken blankett ska jag ta med?",
          en: "Which form should I bring?",
        },
        a: {
          sv: "Utgå från instruktionerna från familjerätten eller adoptionsorganisationen. MFoF har blanketter för hälsodeklaration och läkarintyg vid internationell adoption.",
          en: "Follow the instructions from the family court or adoption organisation. MFoF provides forms for health declarations and medical certificates for international adoption.",
        },
      },
    ],
  },

  /* ─── AFS – handintensivt ────────────────────────────────────── */
  "afs-handintensivt": {
    lead: {
      sv: "Vi går igenom arbetsmoment, belastning och eventuella besvär i händer, armar, skuldror och nacke. Ta med arbetsgivarens underlag så att bedömningen kan kopplas till ditt arbete.",
      en: "We review work tasks, load and any symptoms in hands, arms, shoulders and neck. Bring the employer's documentation so the assessment can be linked to your work.",
    },
    bullets: {
      sv: [
        "Genomgång av arbetsuppgifter, belastning och besvär",
        "Bedömning av behovet av klinisk undersökning av händer, armar, skuldror och nacke",
        "Återkoppling och råd om fortsatt uppföljning",
      ],
      en: [
        "Review of work tasks, load and symptoms",
        "Assessment of need for clinical examination of hands, arms, shoulders and neck",
        "Feedback and advice on continued follow-up",
      ],
    },
    preparation: {
      sv: [
        "Giltig legitimation och arbetsgivarens beställning",
        "Arbetsgivarens riskbedömning och en beskrivning av arbetsuppgifterna",
        "Uppgifter om tidigare medicinska kontroller och eventuell sista giltighetsdag",
        "Aktuell läkemedelslista och relevanta medicinska underlag",
        "Beskriv hur länge och hur ofta du utför de handintensiva momenten",
      ],
      en: [
        "Valid ID and employer's order",
        "Employer's risk assessment and job description",
        "Details of previous medical examinations and any expiry date",
        "Current medication list and relevant medical documentation",
        "Describe how long and how often you perform the hand-intensive tasks",
      ],
    },
  },

  /* ─── AFS – vibrationer ──────────────────────────────────────── */
  "afs-vibrationer": {
    lead: {
      sv: "Läkaren går igenom vilka verktyg du använder, exponeringstiden och eventuella symtom. Berätta vid besöket om till exempel domningar, nedsatt känsel eller fingrar som blir vita i kyla.",
      en: "The doctor reviews which tools you use, the exposure time and any symptoms. Tell us about numbness, reduced sensation or fingers turning white in the cold.",
    },
    bullets: {
      sv: [
        "Genomgång av verktyg, exponering och tidigare arbete",
        "Läkarundersökning med fokus på kärl, nerver och funktion i händer och armar",
        "Bedömning av symtom och råd om fortsatt uppföljning",
      ],
      en: [
        "Review of tools, exposure and previous work",
        "Medical examination focusing on vessels, nerves and function in hands and arms",
        "Assessment of symptoms and advice on continued follow-up",
      ],
    },
    preparation: {
      sv: [
        "Giltig legitimation och arbetsgivarens beställning",
        "Arbetsgivarens riskbedömning och en beskrivning av arbetsuppgifterna",
        "Lista över verktyg, dagliga användningstider och eventuell beräkning av vibrationsexponeringen",
        "Aktuell läkemedelslista och relevanta medicinska underlag",
      ],
      en: [
        "Valid ID and employer's order",
        "Employer's risk assessment and job description",
        "List of tools, daily usage times and any vibration exposure calculation",
        "Current medication list and relevant medical documentation",
      ],
    },
  },

  /* ─── AFS – allergiframkallande ──────────────────────────────── */
  "afs-allergi-kemiska": {
    lead: {
      sv: "Börja med produktnamn och en kort beskrivning av arbetet. Vi återkommer om vilka säkerhetsdatablad och riskbedömningar som behövs, hur kontrollen ska läggas upp och när den kan genomföras.",
      en: "Start with product names and a brief description of the work. We will follow up on which safety data sheets and risk assessments are needed, and how the examination should be arranged.",
    },
    bullets: {
      sv: [
        "Genomgång av kemiska produkter, exponering och hälsa",
        "Undersökning av hud och luftvägar samt lungfunktion",
        "Bedömning och plan för eventuell återkommande kontroll",
      ],
      en: [
        "Review of chemical products, exposure and health",
        "Examination of skin and airways, and lung function",
        "Assessment and plan for any recurring examination",
      ],
    },
    preparation: {
      sv: [
        "Giltig legitimation och arbetsgivarens beställning",
        "Produktnamn, aktuella säkerhetsdatablad samt exponeringstid och arbetssätt",
        "Arbetsgivarens riskbedömning och beskrivning av arbetsuppgifterna",
        "Aktuell läkemedelslista och relevanta medicinska underlag",
      ],
      en: [
        "Valid ID and employer's order",
        "Product names, current safety data sheets, exposure time and work methods",
        "Employer's risk assessment and job description",
        "Current medication list and relevant medical documentation",
      ],
    },
  },

  /* ─── AFS – asbest/kvarts ────────────────────────────────────── */
  "afs-asbest": {
    lead: {
      sv: "Ange vilket damm arbetet gäller och hur många personer som behöver kontroll. Vi går igenom vilka exponeringsuppgifter, tidigare kontroller och eventuella undersökningsresultat som behövs.",
      en: "Specify which dust is involved and how many people need examination. We will review the exposure data, previous examinations and any results needed.",
    },
    bullets: {
      sv: [
        "Genomgång av dammexponering, tidigare arbete och hälsa",
        "Läkarundersökning och bedömning av lungfunktion",
        "Planering av eventuella kompletterande undersökningar",
      ],
      en: [
        "Review of dust exposure, previous work and health",
        "Medical examination and assessment of lung function",
        "Planning of any supplementary examinations",
      ],
    },
    preparation: {
      sv: [
        "Giltig legitimation och arbetsgivarens beställning",
        "Uppgifter om dammtyp, exponeringsmätningar och hur länge arbetet pågår",
        "Tidigare tjänstbarhetsintyg och relevanta lungundersökningar om sådana finns",
        "Aktuell läkemedelslista och relevanta medicinska underlag",
      ],
      en: [
        "Valid ID and employer's order",
        "Details of dust type, exposure measurements and duration of work",
        "Previous fitness certificates and relevant lung examinations if available",
        "Current medication list and relevant medical documentation",
      ],
    },
  },

  /* ─── AFS – bly ──────────────────────────────────────────────── */
  "afs-bly": {
    lead: {
      sv: "Beskriv arbetsmomenten och om det gäller en första kontroll eller uppföljning. Vi återkommer om vilka tidigare provsvar och uppgifter om exponeringen som behövs för planeringen.",
      en: "Describe the work tasks and whether this is a first examination or follow-up. We will follow up on which previous results and exposure details are needed for planning.",
    },
    bullets: {
      sv: [
        "Genomgång av blyexponering och tidigare provtagning",
        "Medicinsk undersökning och bedömning för tjänstbarhetsintyg",
        "Planering av blodprov och fortsatt uppföljning",
      ],
      en: [
        "Review of lead exposure and previous testing",
        "Medical examination and fitness-for-work assessment",
        "Planning of blood tests and continued monitoring",
      ],
    },
    preparation: {
      sv: [
        "Giltig legitimation och arbetsgivarens beställning",
        "Arbetsmoment och uppgifter om möjlig blyexponering",
        "Tidigare blyhalter i blod och datum för senaste medicinska kontrollen",
        "Aktuell läkemedelslista och relevanta medicinska underlag",
      ],
      en: [
        "Valid ID and employer's order",
        "Work tasks and details of possible lead exposure",
        "Previous blood lead levels and date of last medical examination",
        "Current medication list and relevant medical documentation",
      ],
    },
  },

  /* ─── AFS – klättring ────────────────────────────────────────── */
  "afs-klattring": {
    lead: {
      sv: "Beskriv klättringens höjd, arbetsmomenten och möjligheten till räddning. Vi stämmer av om arbetet omfattas av kraven och hur nödvändiga undersökningar ska ordnas.",
      en: "Describe the climbing height, tasks and rescue possibilities. We will check whether the work falls under the requirements and how the necessary examinations should be arranged.",
    },
    bullets: {
      sv: [
        "Genomgång av klättring, arbetsbelastning och hälsa",
        "Läkarundersökning och bedömning av fysisk arbetsförmåga",
        "Planering av de funktions- och hjärtundersökningar som krävs",
      ],
      en: [
        "Review of climbing, workload and health",
        "Medical examination and assessment of physical work capacity",
        "Planning of required functional and cardiac examinations",
      ],
    },
    preparation: {
      sv: [
        "Giltig legitimation och arbetsgivarens beställning",
        "Beskrivning av höjd, klättring och räddningsförhållanden",
        "Tidigare tjänstbarhetsintyg och relevanta undersökningsresultat",
        "Aktuell läkemedelslista och relevanta medicinska underlag",
      ],
      en: [
        "Valid ID and employer's order",
        "Description of height, climbing and rescue conditions",
        "Previous fitness certificates and relevant examination results",
        "Current medication list and relevant medical documentation",
      ],
    },
  },
};

export function getCertDetails(slug: string): CertificateDetails | null {
  return certDetails[slug] ?? null;
}
